import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import React from 'react';
import { DefaultPageViewModel } from '../default-page.viewmodel';
import {
    DEFAULT_CAROUSEL_VIEW,
    GENERAL_SESSIONS_NOTES,
    HOST_ATTENDEES_ENABLED,
    REGISTER_BUTTON_LABEL,
    LAYOUT,
    PRE_REGISTRATION_ENABLED,
    RESTRICT_ACCESS_ENABLED,
    RESTRICT_ACCESS_FILTERS,
    SUBSCRIBE_ATTENDEE_TO_MEETING_ON_REGISTRATION_ENABLED
} from '../../../../../../../../modules/passport-pages/config';
import { FormField } from '../../../../../../../global/form/form-field/form-field';
import { CustomFieldsOptionsListDTO, DefaultCarouselView, SessionPageLayoutOptions } from '../../../../../../../../api/custom-pages.dao';
import Text from '../../../../../../../global/text/text';
import { OEToggle } from '../../../../../../../global/oe-toggle/oe-toggle';
import { RestrictAccessContainer } from '../restrict-access-container';
import { InfoPopover } from '../../../../../../../global/info-popover/info-popover';
import { RichTextEditor } from '../../../../../../../global/rich-text-editor';
import { EnablementStatus } from '../../../../../../../../constants/common.constants';
import {
    ACCESS_TO_GENERAL_SESSIONS_DESCRIPTION_TEXT,
    ACCESS_TO_GENERAL_SESSIONS_NOT_RESTRICTED_TEXT,
    ACCESS_TO_GENERAL_SESSIONS_RESTRICTED_TEXT,
    DEFAULT_CAROUSEL_VIEW_INFO_MSG,
    JOIN_SESSIONS_WITH_PREREGISTERING_TEXT,
    JOIN_SESSIONS_WITHOUT_PREREGISTERING_TEXT,
    SESSION_HOST_ATTENDEES_TOGGLE_TEXT,
    SUBSCRIBE_ON_REGISTRATION_TEXT
} from '../default-pages.texts';
import { defaultCarouselViewOptions } from '../default-pages.const';
import { calendarInvitesOptions } from '../../../passport-settings-form/passport-settings.constants';

type Props = {
    pageVm: DefaultPageViewModel;
    sessionsLayoutOptions: SessionPageLayoutOptions[];
    organizationTypes: string[];
    customFieldsOptionsList: CustomFieldsOptionsListDTO[];
};


export const SessionsPageFields = observer(({
    pageVm,
    sessionsLayoutOptions,
    organizationTypes,
    customFieldsOptionsList,
}: Props): JSX.Element => {
    const { formatMessage } = useIntl();

    const sessionsLayout = pageVm.getFieldByKey(LAYOUT);
    const layoutLabel = formatMessage(pageVm.getFieldConfigByKey(LAYOUT).labelDescriptor);
    const {
        value: restrictAccess,
        touched: restrictAccessTouched,
    } = pageVm.getFieldByKey(RESTRICT_ACCESS_ENABLED);

    const toggleRestrictAccess = () => {
        pageVm.setFieldValueByKey(RESTRICT_ACCESS_ENABLED, !restrictAccess);
        if (restrictAccess) {
            pageVm.setFieldValueByKey(RESTRICT_ACCESS_FILTERS, []);
        }
        if (!restrictAccessTouched) {
            pageVm.setValueTouched(RESTRICT_ACCESS_ENABLED);
        }
    };

    const { value: preRegistrationEnabled } = pageVm.getFieldByKey(PRE_REGISTRATION_ENABLED);
    const togglePreRegistrationEnabled = () => pageVm.setFieldValueByKey(PRE_REGISTRATION_ENABLED, !preRegistrationEnabled);
    const { value: subscribeOnRegistrationEnabled } = pageVm.getFieldByKey(
        SUBSCRIBE_ATTENDEE_TO_MEETING_ON_REGISTRATION_ENABLED
    );
    const toggleSubscribeOnRegistration = () => pageVm.setFieldValueByKey(
        SUBSCRIBE_ATTENDEE_TO_MEETING_ON_REGISTRATION_ENABLED,
        !subscribeOnRegistrationEnabled
    );

    const shouldShowSubscribeToggle = (
        pageVm.passportSettings?.calendarInvite === calendarInvitesOptions.subscribeToCalendar.value ||
        pageVm.passportSettings?.calendarInvite === calendarInvitesOptions.both.value
    );

    const sessionNotesEntity = pageVm.getFieldConfigByKey(GENERAL_SESSIONS_NOTES);
    const sessionNotesLabelText = formatMessage(sessionNotesEntity.labelDescriptor);
    const sessionNotes = pageVm.getFieldByKey(GENERAL_SESSIONS_NOTES);

    const registerButtonLabelEntity = pageVm.getFieldConfigByKey(REGISTER_BUTTON_LABEL);
    const registerButtonLabelText = formatMessage(registerButtonLabelEntity.labelDescriptor);
    const registerButtonLabel = pageVm.getFieldByKey(REGISTER_BUTTON_LABEL);

    const { value: hostAttendeesEnabled } = pageVm.getFieldByKey(HOST_ATTENDEES_ENABLED);
    const toggleHostAttendeesEnabled = () => pageVm.setFieldValueByKey(HOST_ATTENDEES_ENABLED, !hostAttendeesEnabled);

    const defaultCarouselView = pageVm.getFormVmValuePropsByKey(DEFAULT_CAROUSEL_VIEW);
    const defaultCarouselViewLabelText = formatMessage(pageVm.getFieldConfigByKey(DEFAULT_CAROUSEL_VIEW).labelDescriptor);

    const isCarouselLayoutSelected = (value: string): boolean => [
        SessionPageLayoutOptions.CarouselViewByDay,
        SessionPageLayoutOptions.CarouselViewByTrack
    ].some(v => v === value);

    const setSessionsLayout = (value: string): void => {
        pageVm.setFieldValueByKey(LAYOUT, value);

        if (!isCarouselLayoutSelected(value)) {
            defaultCarouselView.setValue(DefaultCarouselView.Collapsed);
        }
    };

    return (
        <>
            <FormField label={
                <div className='flex m-bottom-6px'>
                    {sessionNotesLabelText}
                    <InfoPopover className='m-left-8px'>
                        {sessionNotesEntity.toggleInfoMessage}
                    </InfoPopover>
                </div>
            }>
                <div className='flex'>
                    <RichTextEditor
                        className='oe-form-field__control p-0px m-0px'
                        onBlur={() => pageVm.setValueTouched.bind(pageVm, GENERAL_SESSIONS_NOTES)()}
                        value={sessionNotes.value as string || ''}
                        onChange={(e, editor) => pageVm.setFieldValueByKey(GENERAL_SESSIONS_NOTES, editor.getData())}
                    />
                </div>
                <span className='oe-form-field__error'>
                    {
                        !sessionNotes.valid && sessionNotes.touched &&
                        <FormattedMessage values={{ fieldName: sessionNotesLabelText }} {...sessionNotes.validationMessage} />
                    }
                </span>
            </FormField>

            <FormField>
                <div className='website-custom__checkbox-label m-bottom-0px'>
                    <OEToggle
                        isSelected={hostAttendeesEnabled as boolean}
                        onClick={toggleHostAttendeesEnabled}
                        dataTestId='host-attendees-toggle'
                    />
                    <Text
                        {...SESSION_HOST_ATTENDEES_TOGGLE_TEXT}
                        values={{
                            suffix: hostAttendeesEnabled ? EnablementStatus.ENABLED : EnablementStatus.DISABLED
                        }}
                    />
                </div>
            </FormField>
            <FormField label={layoutLabel}>
                <select
                    className='oe-form-field__control'
                    value={sessionsLayout.value as string}
                    onChange={e => setSessionsLayout(e.target.value)}
                >
                    {sessionsLayoutOptions.map(value => (
                        <option key={value} value={value}>{value}</option>
                    ))}
                </select>
            </FormField>
            {isCarouselLayoutSelected(sessionsLayout.value as string) &&
                <FormField label={
                    <div className='flex m-bottom-6px'>
                        {defaultCarouselViewLabelText}
                        <InfoPopover className='m-left-8px'>
                            {formatMessage(DEFAULT_CAROUSEL_VIEW_INFO_MSG)}
                        </InfoPopover>
                    </div>}
                >
                    <select
                        className='oe-form-field__control'
                        value={defaultCarouselView.value as string}
                        onChange={e => defaultCarouselView.setValue(e.target.value)}
                    >
                        {defaultCarouselViewOptions.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </FormField>
            }
            <FormField>
                <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                    <OEToggle
                        isSelected={preRegistrationEnabled as boolean}
                        onClick={togglePreRegistrationEnabled}
                        dataTestId='general-sessions-pre-registration-toggle'
                    />
                    {preRegistrationEnabled
                        ? <Text {...JOIN_SESSIONS_WITH_PREREGISTERING_TEXT} />
                        : <Text {...JOIN_SESSIONS_WITHOUT_PREREGISTERING_TEXT} />
                    }
                </div>
                {shouldShowSubscribeToggle && (
                        <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                            <OEToggle
                                isSelected={subscribeOnRegistrationEnabled as boolean}
                                onClick={() => toggleSubscribeOnRegistration()}
                                dataTestId='subscribe-on-registration-toggle'
                            />
                            <Text
                                {...SUBSCRIBE_ON_REGISTRATION_TEXT}
                                values={{
                                    suffix: subscribeOnRegistrationEnabled
                                        ? EnablementStatus.ENABLED
                                        : EnablementStatus.DISABLED
                                }}
                            />

                        </div>
                    )}


            </FormField>
            <FormField>
                <div className='website-custom__checkbox-label m-top-15px m-bottom-0px'>
                    <OEToggle
                        isSelected={restrictAccess as boolean}
                        onClick={toggleRestrictAccess}
                    />
                    {restrictAccess
                        ? <Text {...ACCESS_TO_GENERAL_SESSIONS_RESTRICTED_TEXT} />
                        : <Text {...ACCESS_TO_GENERAL_SESSIONS_NOT_RESTRICTED_TEXT} />
                    }
                </div>
                <div className={'font-size-12px line-height-21px m-top-15px'}>
                    <Text {...ACCESS_TO_GENERAL_SESSIONS_DESCRIPTION_TEXT} />
                </div>
            </FormField>
            {restrictAccess
                ? <RestrictAccessContainer
                    pageVm={pageVm}
                    organizationTypes={organizationTypes}
                    maxFiltersCount={20}
                    customFieldsOptionsList={customFieldsOptionsList}
                />
                : null
            }

            <FormField
                label={registerButtonLabelText}
            >
                <input
                    className='oe-form-field__control'
                    value={registerButtonLabel.value as string || ''}
                    onChange={e => pageVm.setFieldValueByKey(REGISTER_BUTTON_LABEL, e.target.value)}
                    onBlur={() => pageVm.setValueTouched(REGISTER_BUTTON_LABEL)}
                />
                <span className='oe-form-field__error'>
                    {
                        !registerButtonLabel.valid && registerButtonLabel.touched &&
                        <FormattedMessage values={{ fieldName: registerButtonLabelText }} {...registerButtonLabel.validationMessage} />
                    }
                </span>
            </FormField>

        </>
    );
});